/**
 * Shared field UI for UserFormDialog (complexity extraction).
 * Module-level components — do not nest inside other components.
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';
import RequiredLabel from './common/RequiredLabel';

type SelectOption = { value: string; label: string };

type UserFormStyledSelectProps = {
  label: string;
  required?: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  value: string;
  options: SelectOption[];
  placeholder: string;
  error?: string;
  showError?: boolean;
  onSelect: (value: string) => void;
};

export function UserFormStyledSelect({
  label,
  required,
  open: _open,
  setOpen: _setOpen,
  containerRef,
  value,
  options,
  placeholder,
  error,
  showError,
  onSelect,
}: Readonly<UserFormStyledSelectProps>) {
  const selectId = `user-form-select-${label.replaceAll(/\s+/g, '-').toLowerCase()}`;

  return (
    <div
      className="create-user-dialog-field create-user-dialog-field-styled-dropdown"
      ref={containerRef}
    >
      <RequiredLabel
        label={label}
        required={required}
      />
      <div
        className={[
          'create-user-select-wrap',
          showError && error ? 'create-user-dialog-input-error' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <select
          id={selectId}
          className="create-user-select-native"
          value={value}
          onChange={e => onSelect(e.target.value)}
          aria-invalid={!!(showError && error)}
        >
          <option
            value=""
            disabled
            hidden
          >
            {placeholder}
          </option>
          {options.map(opt => (
            <option
              key={opt.value}
              value={opt.value}
            >
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className="create-user-select-chevron"
          size={18}
          strokeWidth={2}
          aria-hidden
        />
      </div>
      {showError && error && <span className="create-user-dialog-field-error">{error}</span>}
    </div>
  );
}

type UserFormMobileFieldProps = {
  value: string;
  onChange: (digits: string) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  showError?: boolean;
};

export function UserFormMobileField({
  value,
  onChange,
  onBlur,
  error,
  showError,
}: Readonly<UserFormMobileFieldProps>) {
  return (
    <div className="create-user-dialog-field">
      <RequiredLabel
        label="Mobile No."
        required
      />
      <div
        className={[
          'create-user-dialog-mobile-wrap',
          showError && error ? 'create-user-dialog-input-error' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span
          className="create-user-dialog-mobile-prefix"
          aria-hidden
        >
          +91
        </span>
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          className="create-user-dialog-input"
          value={value}
          onChange={e => onChange(e.target.value.replaceAll(/\D/g, '').slice(0, 10))}
          onBlur={onBlur}
          placeholder="xxxxxxxxxx"
          maxLength={10}
          pattern="[0-9]*"
          aria-label="Mobile number (10 digits)"
          aria-invalid={!!(showError && error)}
        />
      </div>
      {showError && error && <span className="create-user-dialog-field-error">{error}</span>}
    </div>
  );
}

type UserFormDobFieldProps = {
  value: string;
  onTextChange: (formatted: string) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onNativeChange: (apiDate: string) => void;
  formatDobInput: (raw: string) => string;
  dobToNativeValue: (ddmm: string | undefined) => string;
  apiDobToDdMmYyyy: (api: string | undefined) => string;
  pickerRef: React.RefObject<HTMLInputElement | null>;
  error?: string;
  showError?: boolean;
};

export function UserFormDobField({
  value,
  onTextChange,
  onBlur,
  onNativeChange,
  formatDobInput,
  dobToNativeValue,
  apiDobToDdMmYyyy,
  pickerRef,
  error,
  showError,
}: Readonly<UserFormDobFieldProps>) {
  return (
    <div className="create-user-dialog-field create-user-dialog-field-dob">
      <label
        className="create-user-dialog-dob-label"
        htmlFor="user-form-dob-text"
        style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: 500,
          color: 'var(--label-color-default)',
          lineHeight: '20px',
          marginBottom: '4px',
        }}
      >
        Date of Birth <span style={{ color: '#dc2626', marginLeft: '2px' }}>*</span>
      </label>
      <div
        className={[
          'create-user-dialog-dob-input-slot',
          showError && error ? 'create-user-dialog-input-error' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <input
          id="user-form-dob-text"
          type="text"
          className="create-user-dialog-input create-user-dialog-dob-input"
          value={value}
          onChange={e => onTextChange(formatDobInput(e.target.value))}
          onBlur={onBlur}
          placeholder="dd/mm/yyyy"
          aria-label="Date of Birth (dd/mm/yyyy)"
          aria-invalid={!!(showError && error)}
        />
        <input
          ref={pickerRef}
          type="date"
          aria-hidden="true"
          tabIndex={-1}
          value={dobToNativeValue(value)}
          onChange={e => onNativeChange(apiDobToDdMmYyyy(e.target.value))}
          max={new Date().toISOString().split('T')[0]}
          className="create-user-dialog-dob-native-input"
        />
        <button
          type="button"
          className="create-user-dialog-dob-calendar-btn"
          onClick={() => pickerRef.current?.showPicker?.()}
          aria-label="Open calendar"
          title="Pick date"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M15.8333 3.33333H15V1.66667C15 1.20833 14.625 0.833333 14.1667 0.833333C13.7083 0.833333 13.3333 1.20833 13.3333 1.66667V3.33333H6.66667V1.66667C6.66667 1.20833 6.29167 0.833333 5.83333 0.833333C5.375 0.833333 5 1.20833 5 1.66667V3.33333H4.16667C2.875 3.33333 1.91667 4.29167 1.91667 5.58333V15.8333C1.91667 17.125 2.875 18.0833 4.16667 18.0833H15.8333C17.125 18.0833 18.0833 17.125 18.0833 15.8333V5.58333C18.0833 4.29167 17.125 3.33333 15.8333 3.33333ZM15.8333 15.8333H4.16667V8.75H15.8333V15.8333Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
      {showError && error && <span className="create-user-dialog-field-error">{error}</span>}
    </div>
  );
}
